import {useCallback, useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import axios, {AxiosProgressEvent} from "axios";
import {useAuth} from "../auth/AuthHook";
import {CaseNavigationStateBase, SupportCaseState, useSupportCase} from "./CaseHook";
import {useErrorReporter} from "../general/ErrorViewer";
import {EscalationResponse, IssueTracker} from "./model";
import {CaseMessage, CaseMessageRole, CaseMessageType, isNoResponse, NoResponse} from "./Conversation";
import {ActionEntry} from "../input/ActionDropdown";
import {AskIcon, CloseIcon, InvolveHumanIcon, TeachIcon, UnTeachIcon} from "../components/Icons";
import {ChunkedResponseReader} from "./ChunkedResponseReader";
import {withBase} from "../utils/basePath";

type MessageAction = "ask" | "teach";

interface CaseCreationSubmissionState extends CaseNavigationStateBase {
    pendingMessage?: string;
    pendingAction?: MessageAction;
    pendingFile?: File;
}

interface FileUploadResponse {
    file_id: string;
}

interface UseCaseActionsOptions {
    scrollToCenter?: () => void;
}

export function useCaseActions({scrollToCenter = () => undefined}: UseCaseActionsOptions = {}) {
    const {t} = useTranslation();
    const {isLoggedIn, isAdmin, getUserId, getHeaders} = useAuth();
    const {
        supportCaseState,
        updateSupportCase,
        updateSpecificSupportCase,
        caseId,
        addNewMessage,
        fetchData,
        deleteSupportCase,
        fetchCase
    } = useSupportCase();
    const reporter = useErrorReporter();
    const navigate = useNavigate();
    const location = useLocation();
    const [productVersions, setProductVersions] = useState<{[id: string]: string}>({});
    const [showProgress, setShowProgress] = useState(false);
    const [uploadProgressPercent, setUploadProgressPercent] = useState(0);
    const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
    const pendingSubmittedRef = useRef(false);

    const setWaitingForResponse = useCallback((waiting: boolean) => {
        updateSupportCase((previous) => ({
            ...previous,
            isLoading: waiting,
            progress: waiting !== previous.isLoading ? new Map() : previous.progress,
        }));
    }, [updateSupportCase]);

    const updateMessages = useCallback((messages: (CaseMessage | NoResponse)[]) => {
        updateSupportCase((state) => ({
            ...state,
            supportCase: {...state.supportCase!, messages},
        } as SupportCaseState));
    }, [updateSupportCase]);

    const uploadAttachment = useCallback(async (targetCaseId: string, file: File): Promise<string | undefined> => {
        setIsUploadingAttachment(true);
        setUploadProgressPercent(0);
        try {
            const form = new FormData();
            form.append("file", file);
            const response = await axios.post<FileUploadResponse>(withBase(`api/cases/${targetCaseId}/attachments`), form, {
                headers: {
                    ...(await getHeaders()),
                    "Content-Type": "multipart/form-data",
                    "X-File-Name": file.name,
                    "X-File-Size": file.size.toString(),
                    Encoding: "multipart/form-data",
                },
                onUploadProgress: (event: AxiosProgressEvent) => {
                    if (event.total != null) {
                        setUploadProgressPercent(Math.round((100 * event.loaded) / event.total));
                    }
                },
            });
            return response.data.file_id;
        } catch (error) {
            if (error instanceof Error) reporter(error.message);
            return undefined;
        } finally {
            setIsUploadingAttachment(false);
        }
    }, [getHeaders, reporter]);

    const handleNewQuestion = useCallback(async (action: MessageAction, input: string, file?: File): Promise<boolean> => {
        if (caseId == null) return false;
        const fileId = file == null ? undefined : await uploadAttachment(caseId, file);
        if (file != null && fileId == null) return false;

        const message: CaseMessage = {
            text: input,
            messageType: CaseMessageType.USER_MESSAGE,
            messageRole: action === "ask" ? CaseMessageRole.REQUEST : CaseMessageRole.TEACHING,
            requestedBy: await getUserId(),
            attachment_id: fileId,
        };
        const startingMessages = supportCaseState?.supportCase?.messages ?? [];
        setWaitingForResponse(true);
        try {
            setShowProgress(true);
            updateMessages([...startingMessages, message]);
            scrollToCenter();
            const response = await fetchData(`api/cases/${caseId}/${action}?stream=true`, "POST", {
                message: input,
                attachment_id: fileId,
            });
            if (!response.ok || response.status > 299 || response.body == null) {
                updateMessages(startingMessages);
                return false;
            }

            let isFirstChunk = true;
            const reader = new ChunkedResponseReader(response.body.getReader(), (responseMessage: CaseMessage | NoResponse) => {
                if (!isNoResponse(responseMessage) && responseMessage.text.length === 0) return;
                updateMessages([...startingMessages, message, responseMessage]);
                if (isFirstChunk) {
                    setShowProgress(false);
                    isFirstChunk = false;
                    scrollToCenter();
                }
            });
            await reader.streamResponse();
            return true;
        } catch (error) {
            console.error("Error while handling new question:", error);
            updateMessages(startingMessages);
            return false;
        } finally {
            setShowProgress(false);
            setWaitingForResponse(false);
        }
    }, [caseId, fetchData, getUserId, scrollToCenter, setWaitingForResponse, supportCaseState?.supportCase?.messages, updateMessages, uploadAttachment]);

    const handleNewCaseMessage = useCallback(async (action: MessageAction, input: string, file?: File): Promise<boolean> => {
        try {
            setShowProgress(true);
            const response = await fetchData("api/cases/", "POST", {
                title: "New support case",
                severity: "Normal",
                allow_welcome_message: false,
            });
            const newCase = await response.json();
            updateSpecificSupportCase(newCase.case_id, (state) => ({
                ...state,
                supportCase: {
                    ...state.supportCase,
                    case_id: newCase.case_id,
                    title: newCase.title,
                    severity: newCase.severity,
                    status: newCase.status,
                    messages: [],
                },
                isLoading: true,
                isSyncing: false,
            } as SupportCaseState));
            pendingSubmittedRef.current = false;
            navigate(`/cases/${newCase.case_id}`, {
                state: {pendingMessage: input, pendingAction: action, pendingFile: file, preventReload: true} as CaseCreationSubmissionState,
            });
            return true;
        } catch (error) {
            console.error("Error creating new case:", error);
            return false;
        } finally {
            setShowProgress(false);
        }
    }, [fetchData, navigate, updateSpecificSupportCase]);

    const handleNewMessage = useCallback(async (action: MessageAction, input: string, file?: File): Promise<boolean> => {
        if (caseId == null) return handleNewCaseMessage(action, input, file);
        if (Object.keys(productVersions).length === 0) return handleNewQuestion(action, input, file);

        const withVersions = `${input}\n\nHere are the product versions we use:\n\`\`\`json\n${JSON.stringify(productVersions)}\n\`\`\``;
        setProductVersions({});
        return handleNewQuestion(action, withVersions, file);
    }, [caseId, handleNewCaseMessage, handleNewQuestion, productVersions]);

    useEffect(() => {
        const state = location.state as CaseCreationSubmissionState | null;
        if (state?.pendingMessage == null || caseId == null || supportCaseState?.supportCase == null ||
            supportCaseState.supportCase.messages.length !== 0 || pendingSubmittedRef.current) return;

        pendingSubmittedRef.current = true;
        navigate(location.pathname, {replace: true, state: undefined});
        handleNewQuestion(state.pendingAction ?? "ask", state.pendingMessage, state.pendingFile)
    }, [caseId, handleNewQuestion, location.pathname, location.state, navigate, supportCaseState?.supportCase]);

    const handleEscalation = useCallback(async (message: string = ""): Promise<boolean> => {
        try {
            setWaitingForResponse(true);
            const response = await fetchData(`api/cases/${caseId}/escalate`, "POST", {message});
            const parsedResponse: EscalationResponse = await response.json();
            addNewMessage({
                text: parsedResponse.escalation_message.text,
                messageType: CaseMessageType.MESSAGE_RESPONSE,
                messageRole: CaseMessageRole.ESCALATION_RESPONSE,
                timeToRespond: parsedResponse.escalation_message.time_to_respond,
            });
            await fetchCase()
            return true;
        } catch {
            return false;
        } finally {
            setWaitingForResponse(false);
        }
    }, [addNewMessage, caseId, fetchData, setWaitingForResponse, updateSupportCase]);

    const handleClosure = useCallback(async (): Promise<boolean> => {
        try {
            setWaitingForResponse(true);
            await fetchData(`api/cases/${caseId}/close`, "POST");
            updateSupportCase((state) => ({...state, supportCase: {...state.supportCase!, status: "Closed"}}));
            return true;
        } catch {
            return false;
        } finally {
            setWaitingForResponse(false);
        }
    }, [caseId, fetchData, setWaitingForResponse, updateSupportCase]);

    const handleLearn = useCallback(async (action: "propose" | "publish" | "revoke"): Promise<boolean> => {
        setWaitingForResponse(true);
        try {
            await fetchData(`api/cases/${caseId}/extract/${action}`, "POST");
            const actionStateMap = {propose: "proposed", publish: "published", revoke: "draft"};
            updateSupportCase((state) => ({
                ...state,
                supportCase: {
                    ...state.supportCase!,
                    learning_extract: {...state.supportCase?.learning_extract, state: actionStateMap[action]},
                },
            } as SupportCaseState));
            return true;
        } catch {
            return false;
        } finally {
            setWaitingForResponse(false);
        }
    }, [caseId, fetchData, setWaitingForResponse, updateSupportCase]);

    const handleCloseAndPropose = useCallback(async (): Promise<boolean> => {
        const closed = await handleClosure();
        return closed && handleLearn("propose");
    }, [handleClosure, handleLearn]);

    const handleCloseAndLearn = useCallback(async (): Promise<boolean> => {
        const closed = await handleClosure();
        return closed && handleLearn("publish");
    }, [handleClosure, handleLearn]);

    const handleDelete = useCallback(async (): Promise<boolean> => {
        try {
            setWaitingForResponse(true);
            await fetchData(`api/cases/${caseId}`, "DELETE");
            await deleteSupportCase();
            navigate("/");
            reporter(t("Case deleted permanently"));
            return true;
        } catch {
            return false;
        } finally {
            setWaitingForResponse(false);
        }
    }, [caseId, deleteSupportCase, fetchData, navigate, reporter, setWaitingForResponse, t]);

    const buildActionEntries = useCallback((): ActionEntry[] => {
        const entries: ActionEntry[] = [{
            id: "ask",
            label: t("Ask"),
            Icon: AskIcon,
            colorClass: "",
            requiresMessage: true,
            allowsAttachment: true,
            submit: (message, file) => handleNewMessage("ask", message, file),
        }];
        const status = supportCaseState?.supportCase?.status?.toLowerCase();
        if (caseId == null) return entries;

        const messageRoles = supportCaseState?.supportCase?.messages.map((message) => message.messageRole) ?? [];
        const learningState = supportCaseState?.supportCase?.learning_extract?.state;
        const canEscalate = isLoggedIn() && (supportCaseState?.supportCase?.messages.length ?? 0) > 0 &&
            status !== "closed" && status !== "escalated" && supportCaseState?.supportCase?.issue_tracker?.issue_key == null;

        if (status !== "closed") {
            entries.push({
                id: "teach",
                label: t("Teach"),
                Icon: TeachIcon,
                colorClass: "",
                requiresMessage: true,
                allowsAttachment: true,
                submit: (message, file) => handleNewMessage("teach", message, file),
            });
        }
        if (canEscalate) {
            entries.push({
                id: "escalate",
                label: t("Escalate"),
                Icon: InvolveHumanIcon,
                colorClass: "",
                requiresMessage: true,
                allowsAttachment: false,
                submit: handleEscalation,
            });
        }

        const action = (
            id: string,
            label: string,
            Icon: ActionEntry["Icon"],
            submit: ActionEntry["submit"],
        ): ActionEntry => ({id, label, Icon, colorClass: "", requiresMessage: false, allowsAttachment: false, submit});

        if (status !== "closed") {
            const close = action("close", t("Close case"), CloseIcon, handleClosure);
            const closeAndPropose = action("close-and-propose", t("Close and propose for learning"), TeachIcon, handleCloseAndPropose);
            entries.push(...(messageRoles.includes(CaseMessageRole.TEACHING) ? [closeAndPropose, close] : [close, closeAndPropose]));
            if (isAdmin) entries.push(action("close-and-learn", t("Close and learn case"), TeachIcon, handleCloseAndLearn));
        } else {
            if (learningState === "draft" || learningState == null) {
                entries.push(action("propose-learning", t("Propose learning"), TeachIcon, () => handleLearn("propose")));
            }
            if (learningState !== "published" && isAdmin) {
                entries.push(action("publish-learning", t("Publish learning"), TeachIcon, () => handleLearn("publish")));
            }
            if (learningState != null && learningState !== "draft" && isAdmin) {
                entries.push(action("reject-learning", t("Reject learning"), UnTeachIcon, () => handleLearn("revoke")));
            }
        }
        return entries;
    }, [caseId, handleCloseAndLearn, handleCloseAndPropose, handleClosure, handleEscalation, handleLearn, handleNewMessage, isAdmin, isLoggedIn, supportCaseState, t]);

    const handleVersionChange = useCallback((product: string, version: string) => {
        setProductVersions((versions) => {
            const updated = {...versions};
            if (version === "") delete updated[product];
            else updated[product] = version;
            return updated;
        });
    }, []);

    const handleAllVersionSelected = useCallback(async (versions: {product: string, version: string}[]) => {
        await handleNewMessage("ask", `Here are my versions selected:\n\n${versions.map((item) => `- ${item.product}: ${item.version}`).join("\n")}\n`);
        setProductVersions({});
    }, [handleNewMessage]);

    return {
        actionEntries: buildActionEntries(),
        buildActionEntries,
        handleEscalation,
        handleClosure,
        handleLearn,
        handleCloseAndPropose,
        handleCloseAndLearn,
        handleDelete,
        handleVersionChange,
        handleAllVersionSelected,
        productVersions,
        showProgress,
        isUploadingAttachment,
        uploadProgressPercent,
    };
}
